
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBookClass = () => {
    // Trigger Readdy Agent widget for booking
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    } else {
      // Fallback to contact page
      navigate('/contact');
    }
  };

  const achievements = [
    { number: '1000+', label: t('stats.students') },
    { number: '20+', label: t('stats.experience') },
    { number: '95%', label: t('stats.success') },
    { number: '50+', label: t('about.countries') || 'Countries worldwide' }
  ];

  const values = [
    {
      icon: 'ri-heart-line',
      title: t('about.values.passion') || 'Passion for Learning',
      description: t('about.values.passionDesc') || 'We believe learning should be an exciting and inspiring process'
    },
    {
      icon: 'ri-user-heart-line',
      title: t('about.reasons.personalized'),
      description: t('about.reasons.personalizedDesc')
    },
    {
      icon: 'ri-lightbulb-line',
      title: t('about.values.innovation') || 'Innovative Methods',
      description: t('about.values.innovationDesc') || 'We use the latest technologies and teaching methodologies'
    },
    {
      icon: 'ri-trophy-line',
      title: t('about.reasons.results'),
      description: t('about.reasons.resultsDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Motion Video Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-top"
            poster="https://readdy.ai/api/search-image?query=Bright%20vibrant%20educational%20scene%20with%20warm%20lighting%20and%20books%20opening%20gently%2C%20soft%20motion%20of%20pages%20turning%20with%20mathematical%20formulas%20and%20english%20text%20floating%20gracefully%2C%20inspiring%20learning%20atmosphere%20with%20natural%20sunlight%20streaming%20through%20windows%2C%20professional%20educational%20background%20with%20emerald%20green%20and%20teal%20accents%2C%20high%20quality%20motion%20of%20knowledge%20and%20wisdom%20flowing%2C%20peaceful%20and%20inspiring%20educational%20environment&width=1920&height=1080&seq=about-hero-video-poster-v2&orientation=landscape"
          >
            <source src="https://videos.pexels.com/video-files/3196036/3196036-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/3196036/3196036-hd_1920_1080_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/3196036/3196036-sd_640_360_25fps.mp4" type="video/mp4" />
          </video>
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-emerald-500/20 to-teal-600/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {t('about.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 font-medium leading-relaxed drop-shadow-xl max-w-4xl mx-auto">
              {t('about.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="px-12 py-6 text-xl font-bold border-2 border-white/30 rounded-xl"
                onClick={handleBookClass}
              >
                <i className="ri-calendar-check-line mr-3 text-xl"></i>
                {t('cta.bookClass')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('about.meetTeacher') || 'Meet Teacher Iryna'}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.description')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
                {t('about.mission')}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-emerald-600"></i>
                  </div>
                  <span className="text-gray-700">{t('about.qualifications.qualified') || 'Qualified Mathematics Teacher'}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-emerald-600"></i>
                  </div>
                  <span className="text-gray-700">{t('about.qualifications.experience') || 'Over 20 years of teaching experience'}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-emerald-600"></i>
                  </div>
                  <span className="text-gray-700">{t('about.qualifications.online') || 'Expert in online education'}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-emerald-600"></i>
                  </div>
                  <span className="text-gray-700">{t('about.qualifications.founder') || 'Founder of MathLish platform'}</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
               src="/assets/teacher-iryna.jpg"
               alt="Teacher Iryna"
               className="w-full h-auto object-cover block"
               />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl">
                <div className="text-3xl font-bold text-emerald-600">20+</div>
                <div className="text-gray-600">{t('stats.experience')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('stats.title')}
            </h2>
            <p className="text-xl text-emerald-100">
              {t('stats.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  {achievement.number}
                </div>
                <div className="text-lg opacity-90">
                  {achievement.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.ourValues') || 'Our Values'}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.valuesSubtitle') || 'Principles that guide our work'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className={`${value.icon} text-2xl text-emerald-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('cta.readyToStart')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('about.ctaSubtitle') || 'Join our community and reach new heights in learning'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100" onClick={handleBookClass}>
              {t('cta.bookClass')}
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600" onClick={() => navigate('/contact')}>
              {t('cta.contact')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
